import {
  ChangeDetectorRef, ChangeDetectionStrategy, Component, ElementRef, ViewChild,
  NgZone, OnInit, AfterViewInit, OnDestroy,
} from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Location } from '@angular/common';
import { Observable ,  Subscription } from 'rxjs';
import * as _ from 'lodash';



// utils, data
import { LACT } from 'shared/fitments/contextual-actions/config';
import { columns } from 'shared/fitments/search/columns';
import { LIST_FILTER_TYPE } from 'shared/search/active-filters/active-filter-types';
import { REGION_TYPE, VEHICLE_TYPE, SUB_MODEL_TYPE } from 'shared/search/advanced-search/filter-types';
import { createOption } from 'shared/form/select/option-helpers';
import { tooltipTexts } from 'shared/fitments/tooltip-texts';
import { myFitmentsOptions } from 'shared/fitments/fitments-options';
import { groupedAttributes } from 'shared/fitments/search/grouped-attributes';

// models
import { ActiveFilterModel } from 'shared/search/active-filters/active-filter.model';
import { SearchModel } from 'shared/search/search.model';

// components
import { SearchPageComponent } from 'shared/search-page/search-page.component';
import { FitmentsAdvancedSearchComponent } from 'shared/fitments/fitments-advanced-search/fitments-advanced-search.component';
import { SimpleConfirmModalComponent } from 'shared/modal/simple-confirm-modal/simple-confirm-modal.component';
import { FitmentListManagerComponent } from 'shared/fitments/fitments-list-manager.component';
import { FitmentsSearchResultsComponent } from 'shared/fitments/search/search-results/fitments-search-results.component';
import { FitmentsSortingPanelComponent } from 'shared/fitments/search/sorting-panel/fitments-sorting-panel.component';

// services
import { SearchPageService } from 'shared/search-page/search-page.service';
import { FitmentsListService } from 'shared/fitments/fitments-list.service';
import { BrandService } from 'shared/brand/brand.service';
import { ProcessService } from 'shared/process/process.service';
import { ScrollToService } from 'shared/base/scroll-to.service';
import { MyFitmentsSearchService } from 'shared/fitments/search/my-fitments-search.service';
import { AppRoutingService } from '../../app-routing.service';
import { FitmentsService } from 'shared/fitments/fitments.service';
import { PermissionsService } from 'shared/permissions/permissions.service';
import { SavedSearchesService } from 'shared/saved-searches/saved-searches.service';
import { UserService } from 'shared/user/user.service';
import {map, mergeMap, takeWhile} from 'rxjs/operators';
import {fromEvent} from 'rxjs/internal/observable/fromEvent';


@Component({
  moduleId: module.id,
  template: '',
  providers: [
    {provide: 'LIST_TYPE', useValue: 'fitment'},
    FitmentsListService,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FitmentsSearchPageComponent extends SearchPageComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild(FitmentListManagerComponent, {static: false})
  declare listManager: FitmentListManagerComponent;

  @ViewChild(FitmentsAdvancedSearchComponent, {static: false})
  declare advancedSearchComponent: FitmentsAdvancedSearchComponent;

  @ViewChild('cancelColumnBuilder', {static: false})
  cancelColumnBuilder: SimpleConfirmModalComponent | void;

  @ViewChild(FitmentsSearchResultsComponent, {static: false})
  searchResultsComponent: FitmentsSearchResultsComponent | void;

  @ViewChild(FitmentsSortingPanelComponent, {static: false})
  sortingPanel: FitmentsSortingPanelComponent | void;

  @ViewChild('searchResultsScroll', {static: false})
  searchResultsScroll: ElementRef | void;

  actions: any = [];
  ghostValue: string = 'my-fitments';
  searchResultsColumns: any = [];
  rowsHover: boolean = false;
  productsMenuStorageName: string = 'myFitmentsProductsMenu';
  searchDataSource: Observable<any>;
  detailsPageRootRoute: string = 'product-details';
  maxColumnsInSearch: number = myFitmentsOptions.maxColumnsInSearch;
  tooltipTextsMap: any = tooltipTexts;
  initialColumnsOrder: Array<string> = [
    'year', 'make', 'model', 'subModel', 'engineBase',
  ];
  groupedColumns: Array<any> = [
    groupedAttributes.engineBase,
  ];
  hasListManager: boolean = true;
  itemDetailsFields: Array<string> = [
    'descriptions', 'itemId', 'partNumber', 'details',
    '_id', 'updated_at', 'partTerminology'
  ];
  researchNotRemovableColumns: Array<string> = ['make', 'model', 'year'];
  productNotRemovableColumns: Array<string> = [
    'make', 'model', 'year', 'notes', 'mfrLabel', 'position', 'qty', 'partTerminologies',
    'qualifiers', 'digitalAssets',
  ];
  contentSelector: string = '#my-fitments-content';
  showWheelLoader: boolean = true;
  nonProductFieldNames: Array<any>;
  searchResultsScrollLeft: number = 0;
  stickCheckboxes: boolean = false;
  showSidedrawer: boolean = true; // use this field to show sidedrawer
  fitmentsLoaded: boolean = false;
  productColumnsStoreName: string = 'fitmentsColumnsProduct';
  researchColumnsStoreName: string = 'fitmentsColumns';
  searchModelStoreName: string = 'fitmentsSearchModel';
  procuctSearchModelStoreName: string = 'fitmentsSearchModelProduct';
  isResearchMode: boolean = false; // toggles between research and product
  protected scrollLeftBorder: number = 24; // border after which we need to fix first column
  private scrollSub: Subscription;

  /*******************************************
  * COMPONENT GETTERS
  *******************************************/

  get selectedRowsSize(): number|string {
    // if selected all items return total records length
    if (this.allSelected) { return this.totalRecords; }
    return this.selectedRows.length;
  }
  get brandId(): any {
    return this.brandService.getBrand();
  }
  get productItemId(): any {
    return _.get(this.route.snapshot.params, 'id', null);
  }
  get showDefaultStateMessage(): boolean {
    if (this.searchResults.length) { return false; }
    if (this.searchModelFilters.length || (this.searchModel && this.searchModel.productId)) {
      return false;
    }
    return true;
  }
  get filtersCommonName(): string {
    return _.reduce(this.searchModelFilters, (result: string, filter: ActiveFilterModel) => {
      if (filter.type === LIST_FILTER_TYPE) { return result; }
      return `${result} ${filter.title}`;
    }, '');
  }
  get selectedAssignmentValues(): Array<any> {
    const selected = this.allSelected ? this.searchResults : this.selectedRows;
    return _.map(selected, (item: any) => item.assignment_values);
  }
  get selectedAppIds(): Array<any> {
    return _.map(this.selectedRows, (item: any) => item.assignment_values.app_id);
  }

  get storedSearchModel(): SearchModel | null {
    if (_.has(this, 'user.id')) {
      let localStrorageName = `${this.searchModelStoreName}.${this.brandService.getBrand()}.${this.user.id}`;
      if (!this.isResearchMode) {
          localStrorageName = `${this.procuctSearchModelStoreName}.${this.brandService.getBrand()}.${this.user.id}`;
      }
      return JSON.parse(localStorage.getItem(localStrorageName));
    } else {
      return null;
    }
  }

  get storedFitmentsColumns(): Array<{}> | null {
    if (_.has(this, 'user.id')) {
      let localStorageName = `${this.researchColumnsStoreName}.${this.brandService.getBrand()}.${this.user.id}`;
      if (!this.isResearchMode) {
          localStorageName = `${this.productColumnsStoreName}.${this.brandService.getBrand()}.${this.user.id}`;
      }
      return JSON.parse(localStorage.getItem(localStorageName));
    }
    return null;
  }

  get searchResultsWidth(): string {
    return _.get(this, 'searchResultsComponent.componentWidth', '100%');
  }

  get vehicleFilters() {
    if (_.get(this, 'advancedSearch.vehicleFilters')) {
      return this.advancedSearch.vehicleFilters;
    } else if (_.get(this, 'advancedSearchComponent.vehicleFilters')) {
      return this.advancedSearchComponent && this.advancedSearchComponent.vehicleFilters;
    }
    return null;
  }

  constructor(
    public searchService: MyFitmentsSearchService,
    protected brandService: BrandService,
    protected pageService: SearchPageService,
    protected processService: ProcessService,
    protected scrollToService: ScrollToService,
    protected appRoutingService: AppRoutingService,
    protected listsService: FitmentsListService,
    protected permissionsService: PermissionsService,
    protected ngZone: NgZone,
    protected ref: ChangeDetectorRef,
    protected route: ActivatedRoute,
    protected location: Location,
    protected savedSearchesService: SavedSearchesService,
    protected router: Router,
    protected userService: UserService,
    protected fitmentsService: FitmentsService,
  ) {
    super(
      searchService,
      brandService,
      pageService,
      processService,
      scrollToService,
      appRoutingService,
      listsService,
      permissionsService,
      ngZone,
      ref,
      route,
      location,
      savedSearchesService,
      router,
      userService,
    );
  }

  /*******************************************
  * COMPONENT HOOKS
  *******************************************/

  ngOnInit() {
    this.searchService.dataLoadedEvent
    .pipe(takeWhile(() => this.isPageActive))
    .subscribe(() => {
      this.fitmentsLoaded = true;
    });
    this.createSearchDataSource();
    this.fetchFieldsList();
    super.ngOnInit();
  }

  ngOnDestroy() {
    this.fitmentsLoaded = false;
    this.unsubscribeSearchResultsScroll();
    super.ngOnDestroy();
  }

  ngAfterViewInit() {
    this.subscrubeSearchResultsScroll();
  }


  /*******************************************
  * COMPONENT HANDLERS
  *******************************************/

  onPageActionSelect($event: any) {
    super.onPageActionSelect($event);
  }

  onActionSelected($event: any) {
    switch ($event.action) {
      case LACT.ADD_TO_LIST.action:
      case LACT.REMOVE_FROM_LIST.action:
        super.onActionSelected($event);
        break;
      default:
        console.log($event);
    }
  }

  onAdvancedSearchRun() {
    this.showEmptyResultsState = false;
    const vehicleFilters = this.vehicleFilters;
    if (vehicleFilters) { this.addColumnsFromVehicleFilters(vehicleFilters); }
    super.onAdvancedSearchRun();
    this.addColumnsFromVcdbFilters();
    this.storeColumns(this.searchResultsColumns);
    if (!!this.searchModel) {
      this._searchModel.next(this.searchModel);
    }
  }

  onTypeAheadSearchSelect($event: any) {
    const selected = $event.item;
    this.removeAllActiveColumns();
    _.remove(this.searchModelFilters);
    this.advancedSearch.setYearMakeModel({
      make: createOption(selected.make_id, selected.make),
      model: createOption(selected.model_id, selected.model),
      year: createOption(selected.year, selected.year),
    });
    if (!!this.searchModel) {
      this.searchModel.filters = this.advancedSearch.vehicleFilters;
    }
    this.toggleAdvancedSearch(false);
    if (!!this.searchModel) {
      this._searchModel.next(this.searchModel);
    }
  }

  onAdvancedFiltersClear() {
    this.removeAllActiveColumns();
    super.onAdvancedFiltersClear();
  }

  onCancelAdvancedSearchClick() {
    super.onCancelAdvancedSearchClick();
    this.advancedSearch.resetAllData();
  }

  onClearSearchClick() {
    this.removeAllActiveColumns();
    this.deselectAll();
    super.onClearSearchClick();
  }

  onFilterRemove(filter: ActiveFilterModel) {
    this.showEmptyResultsState = false;
    _.defer(() => {
      if (this.searchModel && !this.searchModel.filters.length) {
        this.removeAllActiveColumns();
        this.deselectAll();
      }
      super.onFilterRemove(filter);
    });
  }

  onListSelect(selectedList: any) {
    // reset all filters
    if (this.searchModel) {
      _.remove(this.searchModel.filters);
      this.searchModel.columns = [];
    }
    super.onListSelect(selectedList);
  }

  /*******************************************
  * COMPONENT PUBLIC
  *******************************************/

  addToListRequest(selectedLists: any): Observable<any> {
    if (this.allSelected) {
      return this.listsService.addAllInSearchToListBunch(
        this.searchModel && this.searchService.getQueryString(this.searchModel) || '',
        selectedLists,
        _.get(this.searchModel, 'productId', null),
        this.listId
      );
    } else {
      const data = _.map(this.selectedAssignmentValues, (item: any) => {
        item.columns = this.searchModel && this.searchModel.columns || [];
        return item;
      });
      return this.listsService.addToLists(
        data,
        selectedLists
      );
    }
  }

  removeFromListRequest(): Observable<any> {
    if (this.allSelected) {
      return this.listsService.removeAllInSearchFromList(
        this.searchModel && this.searchService.getQueryString(this.searchModel) || '',
        this.listId
      );
    } else {
      return this.listsService.removeFromList(
        this.selectedAppIds,
        this.listId
      );
    }
  }

  storeSearchModel(searchModel: SearchModel, storageName: string= '') {

    if (_.has(this, 'user.id')) {
      let localStorageName = `${this.searchModelStoreName}.${this.brandService.getBrand()}.${this.user.id}`;
      if (storageName) {
        localStorageName = `${storageName}.${this.brandService.getBrand()}.${this.user.id}`;
      } else if (!this.isResearchMode) {
          localStorageName = `${this.procuctSearchModelStoreName}.${this.brandService.getBrand()}.${this.user.id}`;
      }
      localStorage.setItem(localStorageName, JSON.stringify(searchModel));
    }
  }

  storeColumns(_columns: Array<{}>, storageName: string= '') {
    if (_.has(this, 'user.id')) {
      let localStorageName = `${this.researchColumnsStoreName}.${this.brandService.getBrand()}.${this.user.id}`;
      if (storageName) {
        localStorageName = `${storageName}.${this.brandService.getBrand()}.${this.user.id}`;
      } else if (!this.isResearchMode) {
          localStorageName = `${this.productColumnsStoreName}.${this.brandService.getBrand()}.${this.user.id}`;
      }
      localStorage.setItem(localStorageName, JSON.stringify(_columns));
    }
  }

  changeColumns(selectedColumns: Array<any>): void {
    // create new columns
    super.changeColumns(selectedColumns);
  }

  getDefaultColumns(): any {
    return _.cloneDeep(columns);
  }

  createNewColumns(selectedColumns: any): any {
    const newColumns: Array<any> = super.createNewColumns(selectedColumns);
    const notes: any = _.find(newColumns, ['col', 'notes']);
    if (notes) { notes.type = 'array'; }
    const partTerminologies: any = _.find(newColumns, ['col', 'part_terminologies']);
    if (partTerminologies) { partTerminologies.type = 'objArray'; }
    const qualifiers: any = _.find(newColumns, ['col', 'qualifiers']);
    if (qualifiers) { qualifiers.type = 'objArray'; }
    return newColumns;
  }

  startLoading() {
    if (!this.showWheelLoader) {
      this.isSearchRunning = true;
      return;
    }
    super.startLoading();
  }

  stopLoading() {
    if (!this.showWheelLoader) {
      this.showWheelLoader = true;
      _.defer(() => this.ref.markForCheck());
      this.isSearchRunning = false;
      return;
    }
    super.stopLoading();
  }

  /*******************************************
  * COMPONENT PROTECTED
  *******************************************/

  /*
    * Create request for the predictive search in the search-form.component
  */
  protected createSearchDataSource() {
    this.searchDataSource = new Observable((observer: any) => {
      observer.next(this.typeAheadValue);
    }).pipe(mergeMap((value: string) => {
      return this.fitmentsService.fetchYmmPredictiveSearch(value)
        .pipe(map( res => res.data ));
    }));
  }

  protected resetSearchParams(): void {
    if (!this.searchModel) {
      return;
    }
    this.searchModel.currentPage = 1;
    this.searchModel.sortModel = null;
    this.searchModel.selectAll = 'on-page';
    this.resetSearchService();
    this.searchModel.productId = null;
    this.searchModel.filters = [];
    this.searchModel.columns = [];
    this.searchModel.statusModel = null;
    if (this.listManager) { this.listManager.resetSelectedLists(); }
  }

  protected resetSearchService(): void {
    this.searchService.searchResults = [];
    this.searchService.selectAllChecked = [];
    this.searchService.selectedItems = [];
    this.searchService.allSelected = false;
  }

  protected fetchFieldsList() {
    this.fitmentsService.fetchOptionsFieldsList()
    .subscribe((data: any) => {
      this.fieldNames = _.chain(data)
        .filter((item: any) => !item.item_detail_specific)
        .sortBy('title').value();
      this.nonProductFieldNames = _.filter(this.fieldNames, (item: any) => !item.fitment_02_specific);
    });
  }

  protected assignToProductsRequest(productIds: Array<string|number>): Observable<any> {
    let request;
    // assign all fitments in the search
    if (this.selectAll === 'in-list') {
      if (this.listId) {
        request = this.fitmentsService.assignListToProducts(
          this.brandId,
          productIds,
          this.listId,
          this.searchModel && this.searchService.getQueryString(this.searchModel) || ''
        );
      } else {
        /*
          If the number of apps times the number of products is less than
          (500) then process in the request
        */
       const totalUnits = this.searchService.totalRecords * productIds.length;
       if (totalUnits < 5) {
        request = this.fitmentsService.assignAllAppsInSearchToProducts(
          this.brandId,
          productIds,
          this.searchModel && this.searchService.getQueryString(this.searchModel) || ''
        );
       } else {
          request = this.fitmentsService.assignAllAppsInSearchToProduct(
            this.brandId,
            productIds,
            this.searchModel && this.searchService.getQueryString(this.searchModel) || ''
          );
       }
      }

    } else {

      request = this.fitmentsService.assignToProducts(
        this.brandId,
        productIds,
        this.selectedAssignmentValues,
        this.searchModel && this.searchModel.columns || []
      );
    }
    return request;
  }

  /*
    ****** Column methods ******
  */


  protected getColumnsData(columnNames: Array<string>): any {
    const data: Array<any> = [];
    _.each(columnNames, (name: string) => {
      const column = _.find(this.fieldNames, ['column_name', name]);
      if (!column) {
        return;
      }
      // replace engineLiter on attributes from engine base group
      if (column.column_name === 'engineLiter') {
        data.push(column);
        _.each(groupedAttributes.engineBase.attributes, (engineBaseName: string) => {
          if (engineBaseName === 'engineLiter') {
            return;
          }
          const engineBaseColumn = _.find(this.fieldNames, ['column_name', engineBaseName]);
          data.push(engineBaseColumn);
        });
      } else {
        if (column.column_name === 'partTerminologies') {
          // todo fix it on api side
          column.title_column_name = column.title;
        }
        if (!_.includes(groupedAttributes.engineBase.attributes, column.column_name)) {
          data.push(column);
        }
      }
    });
    return _.chain(data).uniqBy('column_name').compact().value();
  }

  /*
    * Add column to searchResultsColumns and searchModel.columns from filter
  */
  protected addColumnFromFilter(filter: any): void {
    const colSize: number = this.getColumnSize(this.searchResultsColumns);
    const column = this.createColumnFromFilter(filter, colSize);
    const uniqColumns = _.uniq(_.concat(this.searchModel && this.searchModel.columns || [], [column.columnName]));
    // if (columns.length > this.maxColumnsInSearch) return;
    if (colSize !== this.defaultColData.colSize) {
      _.each(this.searchResultsColumns, (item: any) => {
        item.colSize = colSize;
      });
    }
    this.searchResultsColumns.push(column);
    if (!!this.searchModel) {
      this.searchModel.columns = uniqColumns;
    }
  }

  /*
    * Create column data from the added filter
  */
  protected createColumnFromFilter(filter: any, colSize: number): any {
    return Object.assign(
      this.createColumn(
        _.get(filter, 'data.attributeInfo.title_column_name', filter.type),
        _.get(filter, 'data.attributeInfo.column_name', filter.type),
        _.get(filter, 'data.attributeInfo.title'),
        colSize,
      ), {
      filter: filter,
    });
  }

  /*
    * Add columns from vcdb filters
  */
  protected addColumnsFromVcdbFilters(): void {
    let hasEngineBaseColumns = false;
    _.each(this.searchModelVcdbFilters, (filter: ActiveFilterModel) => {
      // don't add already existed columns
      if (_.some(this.searchResultsColumns, ['columnName', filter.data.attributeInfo.column_name])) {
        return;
      }
      // dont' add columns related to engineBase, which should be combined in one group
      if (_.includes(groupedAttributes.engineBase.attributes, filter.data.attributeInfo.column_name)) {
        hasEngineBaseColumns = true;
        return;
      }
      this.addColumnFromFilter(filter);
    });
    // add all columns related to the engineBase group
    if (hasEngineBaseColumns) {
      const baseEngineColumns: Array<any> = [];
      _.each(groupedAttributes.engineBase.attributes, (name: string) => {
        const attributeData = _.find(this.fieldNames, ['column_name', name]);
        if (attributeData) {
          const column = this.createColumnFromLookup(attributeData);
          baseEngineColumns.push(column);
        }
      });
      if (baseEngineColumns.length) {
        this.searchResultsColumns = _.concat(this.searchResultsColumns, baseEngineColumns);
        const columnNames = _.compact(_.map(baseEngineColumns, 'column_name'));
        if (!!this.searchModel) {
          this.searchModel.columns = _.concat(this.searchModel.columns, columnNames);
        }
        // this.rewriteUrl(this.searchModel, this.searchResultsColumns);
      }
    }
  }

  /*
    * Add columns from vehicle filters (used for region and vehicle type)
  */
  protected addColumnsFromVehicleFilters(filters: Array<ActiveFilterModel>): void {
    _.each(filters, (activeFilter: ActiveFilterModel) => {
      _.each(activeFilter.data.options, (filter: ActiveFilterModel) => {
        if (_.includes([REGION_TYPE, VEHICLE_TYPE, SUB_MODEL_TYPE], filter.type)) {
          if (_.some(this.searchResultsColumns, ['columnName', filter.type])) {
            return;
          }
          this.addColumn(filter);
        }
      });
    });
  }

  /*
    * Add column to searchResultsColumns and searchModel.columns from filter
  */
  protected addColumn(filter: any): void {
    const colSize: number = this.getColumnSize(this.searchResultsColumns);
    const column = this.createColumnFromFilter(filter, colSize);
    const uniqColumns = _.uniq(_.concat(this.searchModel && this.searchModel.columns || [], [column.columnName]));
    if (uniqColumns.length > this.maxColumnsInSearch) {
      return;
    }
    if (colSize !== this.defaultColData.colSize) {
      _.each(this.searchResultsColumns, (item: any) => {
        item.colSize = colSize;
      });
    }
    this.searchResultsColumns.push(column);
    if (!!this.searchModel) {
      this.searchModel.columns = uniqColumns;
    }
  }

  protected subscrubeSearchResultsScroll(): void {
    if (!this.searchResultsScroll) {
      return;
    }
    this.ngZone.runOutsideAngular(() => {
      this.scrollSub = this.searchResultsScroll && fromEvent(this.searchResultsScroll.nativeElement, 'scroll')
      .subscribe((event: any) => {
        this.searchResultsScrollLeft = event.currentTarget.scrollLeft;
        if (this.searchResultsScrollLeft >= this.scrollLeftBorder && !this.stickCheckboxes) {
          this.stickCheckboxes = true;
        }
        if (this.searchResultsScrollLeft < this.scrollLeftBorder && this.stickCheckboxes) {
          this.stickCheckboxes = false;
        }
        this.ref.detectChanges();
      });
    });
  }

  protected unsubscribeSearchResultsScroll() {
    if (this.scrollSub) {
      this.scrollSub.unsubscribe();
    }
  }
}
